import React, { useEffect, useState } from "react";
import { ExpandMore } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import type { FollowUpServiceGroup } from "../../../types";
import { formatDateForDisplay } from "../../../utils/dateUtils";
import {
  getFollowUpWhatsAppMessage,
  getWhatsAppButtonStyles,
  getWhatsAppIconStyles,
  sendWhatsAppMessage,
  WHATSAPP_ICON_PATH,
} from "../../../utils/whatsappUtils";
import { Card, ClientCarCard, ErrorState, Icons, Loading } from "../../shared";

function getFollowUpGroupKey(
  group: FollowUpServiceGroup,
  index: number,
): string {
  return `${index}:${group.serviceId}:${group.serviceName}:${group.expirationDays}`;
}

export interface FollowUpClientsPanelProps {
  services: FollowUpServiceGroup[];
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
  onScheduleAppointment: (params: {
    clientId: number;
    serviceId: number;
    clientCarId?: number;
  }) => void;
  onIgnoreSuggestion: (params: {
    clientId: number;
    targetId: number;
    kind: "service" | "subService";
    lastAppointmentId: number;
  }) => void | Promise<void>;
  ignoringKey?: string | null;
}

export const FollowUpClientsPanel: React.FC<FollowUpClientsPanelProps> = ({
  services,
  loading,
  error,
  onRetry,
  onScheduleAppointment,
  onIgnoreSuggestion,
  ignoringKey = null,
}) => {
  const navigate = useNavigate();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(),
  );

  const totalClients = services.reduce(
    (sum, group) => sum + group.clients.length,
    0,
  );

  const showEmpty = !loading && !error && totalClients === 0;

  // Drop expanded ids for groups that no longer exist; never auto-expand.
  useEffect(() => {
    if (!loading && !error) {
      const visibleKeys = new Set(
        services
          .filter((group) => group.clients.length > 0)
          .map((group, index) => getFollowUpGroupKey(group, index)),
      );

      setExpandedGroups((prev) => {
        const next = new Set<string>();
        prev.forEach((key) => {
          if (visibleKeys.has(key)) next.add(key);
        });
        if (next.size === prev.size) return prev;
        return next;
      });
    }
  }, [services, loading, error]);

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {loading && (
        <Card className="p-6 border border-gray-100 shadow-sm">
          <Loading message="Carregando clientes para retorno…" size="md" className="py-8" />
        </Card>
      )}

      {!loading && error && (
        <Card className="p-6 border border-gray-100 shadow-sm">
          <ErrorState
            message={error}
            variant="inline"
            onRetry={onRetry}
            className="py-4"
          />
        </Card>
      )}

      {showEmpty && (
        <Card className="p-10 sm:p-14 border border-gray-100 shadow-sm">
          <div className="flex flex-col items-center text-center max-w-md mx-auto">
            <Icons.Check sx={{ fontSize: 56 }} className="text-emerald-400" />
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              Nenhum retorno pendente
            </h2>
            <p className="mt-2 text-gray-600 text-sm sm:text-base leading-relaxed">
              Todos os clientes estão dentro da validade dos serviços cadastrados.
            </p>
          </div>
        </Card>
      )}

      {!loading &&
        !error &&
        services.map((group, index) => {
          if (group.clients.length === 0) return null;

          const groupKey = getFollowUpGroupKey(group, index);
          const isExpanded = expandedGroups.has(groupKey);

          return (
            <Card
              key={groupKey}
              className="p-4 sm:p-6 border border-gray-100 shadow-sm"
            >
              <button
                type="button"
                onClick={() => toggleGroup(groupKey)}
                className="w-full flex items-start justify-between gap-3 text-left rounded-lg hover:bg-gray-50/80 dark:hover:bg-slate-700/60 -mx-1 px-1 py-1 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
                aria-expanded={isExpanded}
                aria-controls={`follow-up-group-${groupKey}`}
              >
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {group.serviceName}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
                    Validade: {group.expirationDays} dias ·{" "}
                    <span className="font-medium text-sky-700 dark:text-sky-400">
                      {group.clients.length}{" "}
                      {group.clients.length === 1 ? "cliente" : "clientes"}
                    </span>
                  </p>
                </div>
                <ExpandMore
                  sx={{
                    fontSize: 28,
                    color: "#6b7280",
                    flexShrink: 0,
                    transition: "transform 0.2s",
                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                  aria-hidden
                />
              </button>

              {isExpanded && (
              <ul
                id={`follow-up-group-${groupKey}`}
                className="mt-4 space-y-3"
              >
                {group.clients.map((row) => {
                  const { client, clientCar } = row;
                  const hasPhone = Boolean(client.phoneNumber?.trim());

                  const followUpMessage = (
                    <p>
                      Último atendimento:{" "}
                      <span className="font-medium text-gray-700">
                        {formatDateForDisplay(row.lastAppointmentDate)}
                      </span>
                      {" · "}
                      <span className="font-semibold text-amber-700">
                        {row.daysSinceLastAppointment} dias atrás
                      </span>
                    </p>
                  );

                  const rowIgnoreKey = `${group.kind}:${group.serviceId}:${row.lastAppointmentId}`;
                  const isIgnoring = ignoringKey === rowIgnoreKey;

                  const followUpActions = (
                    <div className="flex items-center gap-2">
                      {hasPhone ? (
                        <button
                          type="button"
                          onClick={() =>
                            sendWhatsAppMessage({
                              phoneNumber: client.phoneNumber,
                              message: getFollowUpWhatsAppMessage(
                                client.name,
                                group.serviceName,
                                row.daysSinceLastAppointment,
                                group.expirationDays,
                              ),
                            })
                          }
                          className={getWhatsAppButtonStyles("sm")}
                          title="Enviar mensagem no WhatsApp"
                          aria-label={`Enviar mensagem de retorno por WhatsApp para ${client.name}`}
                        >
                          <svg
                            className={getWhatsAppIconStyles("sm")}
                            fill="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden
                          >
                            <path d={WHATSAPP_ICON_PATH} />
                          </svg>
                        </button>
                      ) : null}

                      <button
                        type="button"
                        onClick={() =>
                          onScheduleAppointment({
                            clientId: client.id,
                            serviceId: group.serviceId,
                            clientCarId: clientCar?.id,
                          })
                        }
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-sky-500 hover:bg-sky-600 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
                        title="Agendar atendimento"
                        aria-label={`Agendar atendimento para ${client.name}`}
                      >
                        <Icons.Event sx={{ fontSize: 16 }} />
                        <span>Agendar</span>
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          onIgnoreSuggestion({
                            clientId: client.id,
                            targetId: group.serviceId,
                            kind: group.kind ?? "service",
                            lastAppointmentId: row.lastAppointmentId,
                          })
                        }
                        disabled={isIgnoring || row.lastAppointmentId == null}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-800 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 border border-gray-200 dark:border-slate-600 text-xs sm:text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Cliente recusou — ocultar até o próximo atendimento deste serviço"
                        aria-label={`Ignorar sugestão de retorno para ${client.name}`}
                      >
                        <Icons.VisibilityOff sx={{ fontSize: 16 }} />
                        <span>{isIgnoring ? "Ignorando…" : "Ignorar"}</span>
                      </button>
                    </div>
                  );

                  return (
                    <li key={`${group.serviceId}-${client.id}-${clientCar?.id ?? "none"}`}>
                      {clientCar ? (
                        <ClientCarCard
                          car={clientCar}
                          size="md"
                          clientName={client.name}
                          serviceName={group.serviceName}
                          message={followUpMessage}
                          actions={followUpActions}
                          onClick={() => navigate(`/admin/clients/${client.id}`)}
                        />
                      ) : (
                        <div className="flex flex-col gap-3 rounded-2xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-3 sm:px-4 hover:shadow-md hover:border-gray-200 dark:hover:border-slate-500 transition-all duration-200">
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/clients/${client.id}`)}
                            className="min-w-0 text-left"
                          >
                            <p className="font-medium text-gray-900 dark:text-white truncate">
                              {client.name}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              {group.serviceName}
                            </p>
                          </button>
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1 text-xs text-gray-500">
                              {followUpMessage}
                            </div>
                            <div className="shrink-0">{followUpActions}</div>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              )}
            </Card>
          );
        })}
    </div>
  );
};
